import { SubChecklistCard } from 'types/electionService';
import { completeSubscriberChecklistStep, getSubscriberById } from 'api/operations/electionService';
import { action, autorun, computed, makeObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { UploadDocumentModalStore } from 'store/modules/upload-documents/ui-stores/uploadDocumentModalStore';
import { UploadDocumentsModule } from 'store/modules/upload-documents/uploadDocumentsModule';
import UploadDocumentsPageStore, {
  StepInteractor,
  UploadDocumentsDomainData,
} from 'store/modules/upload-documents/uploadDocumentsPageStore';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { UploadStep } from 'types/documentsService';
import { ChecklistOption } from 'types/enrollmentService';
import SubscriberDelegate from 'store/modules/elections/subscriberDelegate';

// Note: `entityId` represents Sub ID + Cart ID
interface DomainData extends UploadDocumentsDomainData {
  groupId: string;
  affiliatedId: string;
}

export default class SubscriberIMQDocumentsPageStore extends UploadDocumentsPageStore implements StepInteractor {
  private readonly _rootStore: RootStore;
  private readonly domainData: DomainData;
  private _subscriberDelegate: SubscriberDelegate | null = null;
  private _subscriberId: string;

  public get checklistOptions(): ChecklistOption<SubChecklistCard>[] {
    return this._subscriberDelegate?.getTaskChecklistOptions(this.domainData.affiliatedId) || [];
  }

  // Overriding parent method to use sponsorId from the user store instead of the enrollment period,
  // which is not loaded in this context
  protected _createDocumentUploadModalStore(): void {
    this.uploadDocumentModalStore = new UploadDocumentModalStore({
      uploadedDocumentsTypes: this._uploadDocumentsModule.documentsTitles,
      onSubmit: this.loadInitialData,
      setPageIsLoading: this._setIsLoading,
      entityId: this._domainData.entityId,
      enrollmentSponsorId: this._rootStore.userStore.sponsorId,
      groupName: this._group?.name || '',
    });
  }

  private async _getSubscriber(): Promise<void> {
    const subscriber = await getSubscriberById(this._subscriberId);
    runInAction(() => {
      this._subscriberDelegate = new SubscriberDelegate({ subscriber, periodId: this._domainData.entityId });
    });
  }

  public async loadInitialData(): Promise<void> {
    this._setIsLoading(true);

    await Promise.all([
      this._fetchGroupInfo(this.domainData.groupId),
      this._getSubscriber(),
      this._uploadDocumentsModule.loadInitialData(),
    ]);

    // Autorun ensures the mobx reactive context for the actions inside
    autorun(() => {
      this._createDocumentUploadModalStore();
    });
    this._setIsLoading(false);
  }

  public async completeStep(): Promise<void> {
    this._setIsLoading(true);
    await this._uploadDocumentsModule.submitDocuments();
    await completeSubscriberChecklistStep({
      subId: this._subscriberId,
      entityId: this.domainData.affiliatedId,
      card: SubChecklistCard.SUBSCRIBER_DOCUMENTS_IMQ,
    });
    runInAction(() => {
      this._setIsLoading(false);
    });
  }

  // This page does give ability to uncomplete step so far.
  // eslint-disable-next-line class-methods-use-this
  public uncompleteStep(): Promise<void> {
    throw new Error('uncompleteStep method is not implemented on this page.');
  }

  // eslint-disable-next-line class-methods-use-this
  public get thisStepHasBeenCompleted(): boolean {
    return false;
  }

  constructor(domainData: DomainData) {
    super(domainData, UploadStep.SUBSCRIBER_IMQ);
    this.domainData = domainData;
    this._rootStore = getRootStoreInstance();
    // Note: `entityId` represents Sub ID + Cart ID
    this._subscriberId = domainData.entityId.split('_')[0];

    this._uploadDocumentsModule = new UploadDocumentsModule({
      entityId: this._domainData.entityId,
      uploadStep: UploadStep.SUBSCRIBER_IMQ,
      affiliatedId: domainData.affiliatedId,
    });

    makeObservable<SubscriberIMQDocumentsPageStore>(this, {
      thisStepHasBeenCompleted: computed,
      checklistOptions: computed,
      completeStep: action.bound,
      uncompleteStep: action.bound,
    });
  }
}

const SubscriberIMQDocumentsPageStoreContext = createContext(
  new SubscriberIMQDocumentsPageStore({
    entityId: 'stub-entity-id',
    affiliatedId: 'stub-affiliated-id',
    groupId: 'stub-group-id',
  }),
);

export const useSubscriberIMQDocumentsPageStore = (): SubscriberIMQDocumentsPageStore => {
  return useContext(SubscriberIMQDocumentsPageStoreContext);
};

export { SubscriberIMQDocumentsPageStoreContext };
